import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { Box, FloaterTooltipMarkdown, H6, Icon, Link, Optional, useMediaQueries, } from '@honda-canada/design-system-react';
import { InView } from 'react-intersection-observer';
import { SplideSlide } from '@splidejs/react-splide';
import { useTranslation } from 'react-i18next';
import AccessoryCard from './shared/AccessoryCard';
import CarouselSlider from '../MinimalCarousel';
import Context from './service/Context';
import Error from '../PriceComponent/Error';
import Loader from '../../utils/components/Loader';
import SubCategoriesDropdown from './shared/SubCategoriesDropdown';
import ViewMoreCard from './shared/ViewMoreCard';
import gtmForGenericCtaLinkClick from '../../core/gtm/gtmForGenericCtaLinkClick';
import gtmSliderArrowClick from '../../core/gtm/gtmSliderArrowClick';
import gtmViewAllOptionsClick from '../../core/gtm/gtmViewAllOptionsClick';
import themeStyles from './AccessoriesCardPanel.styles';
import { CHARGING, IN_VIEW_ROOT_MARGIN, IN_VIEW_STYLE, IN_VIEW_THRESHOLD, WHEELS } from './utils/constants';
import { EqualHeight } from '../../utils/components/EqualHeight';
import { removeAllTagsFromMarkdown } from '../../utils/markdown';
import { selectBodyTypesForGtm, selectCurrentVehicleFromVehicles } from '../../core/reducers/vehicles';
import { selectCurrentVehicle } from '../../core/reducers/currentVehicle';
import { setCurrentViewMoreModalId } from '../../core/reducers/ui';
import { useAppDispatch, useAppSelector } from '../../core/utils';
const CAROUSEL_STYLES = {
    ml: ['-12px', '-12px', '-10px'],
    mr: ['-12px', '-12px', '-6px'],
};
const MIN_ITEMS_FOR_ARROWS = 2;
const AccessoryCardContainer = themeStyles.apply(Box, 'AccessoryCardContainer');
const ViewMoreAccessoriesCta = themeStyles.apply(Link, 'ViewMoreAccessoriesCta');
const CarouselWrapper = themeStyles.apply(Box, 'CarouselWrapper');
const AccessoryCardPanel = ({ label, tooltipText, viewMoreLabel, accessoryCards, maxCardsDisplayed = 8, categoryId, setSelectedSubCategory, selectedSubCategory, categoryOptions, targetSlideIndex, handleInViewChange, defaultSubCategory, showErrorMessage, showLoader, shouldSectionBeHidden, componentName, }) => {
    const { scrollTrigger, setScrollTrigger, scrollPosition, setConfigurePreviousPosition } = useContext(Context);
    const { isDesktop } = useMediaQueries();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const isMounted = useRef(false);
    const splideOptions = {
        fixedWidth: 'none',
        gap: isDesktop ? '16px' : '8px',
    };
    const getViewMoreTitleByCategory = (category) => {
        switch (category) {
            case WHEELS:
                return t('Pages.Bap.additionalWheelsLabel');
            case CHARGING:
                return t('Pages.Bap.additionalChargingLabel');
            default:
                return t('Pages.Bap.additionalAccessoriesLabel');
        }
    };
    const gtmBodyStyle = useAppSelector(selectBodyTypesForGtm);
    const { inventoryStatus } = useAppSelector(selectCurrentVehicle);
    const currentVehicle = useAppSelector(selectCurrentVehicleFromVehicles);
    useEffect(() => {
        isMounted.current = true;
        if (setConfigurePreviousPosition) {
            setConfigurePreviousPosition(0);
        }
    }, []);
    useLayoutEffect(() => {
        if (typeof window !== 'undefined') {
            if (scrollTrigger) {
                window.scrollTo(0, scrollPosition);
                setScrollTrigger(false);
            }
        }
    }, [scrollTrigger, scrollPosition, setScrollTrigger]);
    const gtmPayload = {
        model: currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.modelGtmName,
        trim: removeAllTagsFromMarkdown(currentVehicle === null || currentVehicle === void 0 ? void 0 : currentVehicle.trimGtmName),
        gtmBodyStyle,
    };
    const handleViewMoreClick = () => {
        if (setSelectedSubCategory) {
            setSelectedSubCategory(t('Pages.Bap.allAccessoriesLabel'), true);
        }
        dispatch(setCurrentViewMoreModalId(categoryId));
        gtmViewAllOptionsClick({
            ...gtmPayload,
            sectionName: categoryId,
        });
    };
    const showAccessoryCards = (accessoryCards === null || accessoryCards === void 0 ? void 0 : accessoryCards.length) && !showErrorMessage;
    const isSectionHidden = shouldSectionBeHidden && !showAccessoryCards && !showLoader;
    if (isSectionHidden)
        return null;
    const renderCarouselItems = () => {
        if (!accessoryCards)
            return null;
        const items = accessoryCards.slice(0, maxCardsDisplayed).map(({ businessKey, ...others }) => (_jsx(SplideSlide, { children: _jsx(AccessoryCard, { businessKey: businessKey, ...others }) }, businessKey)));
        if (accessoryCards.length > maxCardsDisplayed) {
            items.push(_jsx(SplideSlide, { children: _jsx(ViewMoreCard, { handleViewMoreClick: handleViewMoreClick, isHorizontal: false, headingTitle: getViewMoreTitleByCategory(categoryId) }) }, "show-more"));
        }
        return items;
    };
    return (_jsx(InView, { as: "div", onChange: inView => handleInViewChange(inView, categoryId), id: `${categoryId}-section`, rootMargin: IN_VIEW_ROOT_MARGIN, style: IN_VIEW_STYLE, threshold: IN_VIEW_THRESHOLD, children: _jsxs(AccessoryCardContainer, { "data-testid": `cy-accessory-card-panel-${categoryId}`, children: [_jsxs(H6, { as: "h3", "data-testid": `cy-config-${label}`, id: `${categoryId}-title`, children: [label, _jsx(Optional, { when: tooltipText, children: _jsx(FloaterTooltipMarkdown, { "data-testid": "cy-config-info", as: "span", content: tooltipText, ariaLabel: label, ml: "xs", styling: "light", onVisibilityChange: () => {
                                    if (!isMounted.current)
                                        return;
                                    gtmForGenericCtaLinkClick({
                                        ...gtmPayload,
                                        ctaType: 'click',
                                        eventName: 'cta_click',
                                        linkText: tooltipText,
                                        componentType: componentName,
                                        customizeSection: categoryId,
                                        availabilityFlag: inventoryStatus,
                                    });
                                }, children: ({ active }) => _jsx(Icon, { name: "information", filled: active }) }) })] }), _jsx(Optional, { when: showLoader, children: _jsx(Loader, { testId: "cy-loading-state" }) }), _jsx(Optional, { when: showErrorMessage, children: _jsx(Error, { message: t('Pages.Bap.apiLoadingError'), verticalAlignment: "center", errorIconName: "exclamationAlert" }) }), _jsxs(Optional, { when: showAccessoryCards, children: [_jsx(Optional, { when: viewMoreLabel, children: _jsxs(ViewMoreAccessoriesCta, { styling: "tertiary2", onClick: handleViewMoreClick, "data-testid": "view-more-cta", children: [viewMoreLabel, _jsx(Icon, { name: "arrowRight", ml: "xs", iconColor: "blue" })] }) }), _jsx(SubCategoriesDropdown, { defaultValue: defaultSubCategory, selectedValue: selectedSubCategory, setSelectedSubCategory: setSelectedSubCategory, options: categoryOptions }), _jsx(EqualHeight, { children: _jsx(CarouselWrapper, { children: _jsx(CarouselSlider, { arrowColor: "black", length: (accessoryCards === null || accessoryCards === void 0 ? void 0 : accessoryCards.length) || 0, splideOptions: splideOptions, carouselStyles: CAROUSEL_STYLES, showArrows: ((accessoryCards === null || accessoryCards === void 0 ? void 0 : accessoryCards.length) || 0) > MIN_ITEMS_FOR_ARROWS, targetSlideIndex: targetSlideIndex, onSliderArrowClick: () => {
                                        gtmSliderArrowClick({
                                            ...gtmPayload,
                                            sectionName: categoryId,
                                            availabilityFlag: inventoryStatus,
                                        });
                                    }, children: renderCarouselItems }) }) })] })] }) }));
};
export default AccessoryCardPanel;
